import React from 'react'
import type { FormNotificationSettings } from '../../types/forms'

export const DEFAULT_FORM_NOTIFICATIONS: FormNotificationSettings = {
  dailyReminder: {
    enabled: false,
    afterHours: 5,
    resendAfterHours: 2,
    maxSends: 3,
    notifyUser: true,
    notifyFan: false,
    email: false,
    push: false,
    alert: true,
  },
  pending: {
    enabled: false,
    email: false,
    push: false,
    alert: true,
  },
  completed: {
    enabled: false,
    email: false,
    push: false,
    alert: true,
    notifyUser: true,
    notifyFan: false,
  },
}

export function normalizeFormNotifications(raw: Partial<FormNotificationSettings> | null | undefined): FormNotificationSettings {
  const daily = raw?.dailyReminder
  const pending = raw?.pending
  const completed = raw?.completed
  return {
    dailyReminder: { ...DEFAULT_FORM_NOTIFICATIONS.dailyReminder, ...daily },
    pending: { ...DEFAULT_FORM_NOTIFICATIONS.pending, ...pending },
    completed: { ...DEFAULT_FORM_NOTIFICATIONS.completed, ...completed },
  }
}

type ChannelKey = 'email' | 'push' | 'alert'

function ChannelChecks({
  value,
  onChange,
}: {
  value: { email: boolean; push: boolean; alert: boolean }
  onChange: (next: { email: boolean; push: boolean; alert: boolean }) => void
}) {
  const set = (key: ChannelKey, checked: boolean) => {
    const next = { ...value, [key]: checked }
    if (key === 'push' && checked) next.alert = true
    if (key === 'alert' && !checked) next.push = false
    onChange(next)
  }
  return (
    <div className="flex flex-wrap gap-4">
      {([
        ['email', 'Email'],
        ['push', 'Push'],
        ['alert', 'Inbox'],
      ] as const).map(([key, label]) => (
        <label key={key} className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="checkbox checkbox-sm"
            checked={!!value[key]}
            onChange={(e) => set(key, e.target.checked)}
          />
          {label}
        </label>
      ))}
    </div>
  )
}

type Props = {
  accessLevels: string[]
  levelNames: string[]
  levelsLoading: boolean
  legacyAccess: boolean
  notifications: FormNotificationSettings
  jtFolder: string
  onAccessLevels: (levels: string[]) => void
  onNotifications: (next: FormNotificationSettings) => void
}

const FormAccessAndNotifications: React.FC<Props> = ({
  accessLevels,
  levelNames,
  levelsLoading,
  legacyAccess,
  notifications,
  jtFolder,
  onAccessLevels,
  onNotifications,
}) => {
  const toggleLevel = (name: string, checked: boolean) => {
    onAccessLevels(checked ? [...accessLevels, name] : accessLevels.filter((level) => level !== name))
  }
  const folder = jtFolder.trim()

  return (
    <div className="md:col-span-2 space-y-4">
      <div>
        <label className="label"><span className="label-text font-medium">Who can use this form</span></label>
        <div className="p-3 rounded-lg border border-base-300">
          {legacyAccess && (
            <p className="text-xs text-warning mb-2">
              This form still uses its old permission list. Check the access levels that should use it, then save.
              Leaving every box empty means nobody can fill it.
            </p>
          )}
          {levelsLoading ? (
            <p className="text-sm text-base-content/70">Loading access levels...</p>
          ) : levelNames.length === 0 ? (
            <p className="text-sm text-base-content/70">No access levels found.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
              {levelNames.map((name) => (
                <label key={name} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm"
                    checked={accessLevels.includes(name)}
                    onChange={(e) => toggleLevel(name, e.target.checked)}
                  />
                  <span className="text-sm">{name}</span>
                </label>
              ))}
            </div>
          )}
          {!legacyAccess && (
            <p className="text-xs text-base-content/60 mt-2">
              Anyone whose access level is checked can fill this form. None checked means the form is hidden.
            </p>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="font-medium">Notifications for this form</h3>
        <p className="text-xs text-base-content/60">
          These replace the old admin-wide form notices. Push also turns on Inbox.
        </p>

        <div className="p-3 rounded-lg border border-base-300 space-y-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              className="checkbox checkbox-sm"
              checked={notifications.dailyReminder.enabled}
              onChange={(e) => onNotifications({
                ...notifications,
                dailyReminder: { ...notifications.dailyReminder, enabled: e.target.checked },
              })}
            />
            <span className="font-medium text-sm">Daily reminder while clocked in</span>
          </label>
          <p className="text-xs text-base-content/60">
            {folder
              ? `Looks for today’s file in the JobTread folder “${folder}”.`
              : 'Add a JobTread folder in the upload settings or this reminder will not run.'}
          </p>
          {notifications.dailyReminder.enabled && (
            <div className="grid gap-3 sm:grid-cols-3">
              <label className="form-control">
                <span className="label-text text-xs">Send after (hours)</span>
                <input
                  type="number"
                  min={1}
                  max={168}
                  step={0.5}
                  className="input input-bordered input-sm"
                  value={notifications.dailyReminder.afterHours}
                  onChange={(e) => onNotifications({
                    ...notifications,
                    dailyReminder: { ...notifications.dailyReminder, afterHours: Number(e.target.value) || 1 },
                  })}
                />
              </label>
              <label className="form-control">
                <span className="label-text text-xs">Wait before again (hours)</span>
                <input
                  type="number"
                  min={0.25}
                  max={168}
                  step={0.25}
                  className="input input-bordered input-sm"
                  value={notifications.dailyReminder.resendAfterHours}
                  onChange={(e) => onNotifications({
                    ...notifications,
                    dailyReminder: { ...notifications.dailyReminder, resendAfterHours: Number(e.target.value) || 0.25 },
                  })}
                />
              </label>
              <label className="form-control">
                <span className="label-text text-xs">Max sends per shift</span>
                <input
                  type="number"
                  min={1}
                  max={50}
                  step={1}
                  className="input input-bordered input-sm"
                  value={notifications.dailyReminder.maxSends}
                  onChange={(e) => onNotifications({
                    ...notifications,
                    dailyReminder: { ...notifications.dailyReminder, maxSends: Math.max(1, Math.round(Number(e.target.value) || 1)) },
                  })}
                />
              </label>
              <div className="sm:col-span-3 space-y-2">
                <ChannelChecks
                  value={notifications.dailyReminder}
                  onChange={(next) => onNotifications({
                    ...notifications,
                    dailyReminder: { ...notifications.dailyReminder, ...next },
                  })}
                />
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm"
                    checked={notifications.dailyReminder.notifyUser}
                    onChange={(e) => onNotifications({
                      ...notifications,
                      dailyReminder: { ...notifications.dailyReminder, notifyUser: e.target.checked },
                    })}
                  />
                  The person who is clocked in
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm"
                    checked={notifications.dailyReminder.notifyFan}
                    onChange={(e) => onNotifications({
                      ...notifications,
                      dailyReminder: { ...notifications.dailyReminder, notifyFan: e.target.checked },
                    })}
                  />
                  Everyone else on the access levels above
                </label>
              </div>
            </div>
          )}
        </div>

        <div className="p-3 rounded-lg border border-base-300 space-y-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              className="checkbox checkbox-sm"
              checked={notifications.pending.enabled}
              onChange={(e) => onNotifications({
                ...notifications,
                pending: { ...notifications.pending, enabled: e.target.checked },
              })}
            />
            <span className="font-medium text-sm">Waiting on the next person</span>
          </label>
          <p className="text-xs text-base-content/60">
            Sent to whoever’s turn it is after someone else finishes their step.
          </p>
          {notifications.pending.enabled && (
            <ChannelChecks
              value={notifications.pending}
              onChange={(next) => onNotifications({
                ...notifications,
                pending: { ...notifications.pending, ...next },
              })}
            />
          )}
        </div>

        <div className="p-3 rounded-lg border border-base-300 space-y-3">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              className="checkbox checkbox-sm"
              checked={notifications.completed.enabled}
              onChange={(e) => onNotifications({
                ...notifications,
                completed: { ...notifications.completed, enabled: e.target.checked },
              })}
            />
            <span className="font-medium text-sm">Form completed</span>
          </label>
          <p className="text-xs text-base-content/60">
            Sent when a form with more than one filler is fully done.
          </p>
          {notifications.completed.enabled && (
            <>
              <ChannelChecks
                value={notifications.completed}
                onChange={(next) => onNotifications({
                  ...notifications,
                  completed: { ...notifications.completed, ...next },
                })}
              />
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  checked={notifications.completed.notifyUser}
                  onChange={(e) => onNotifications({
                    ...notifications,
                    completed: { ...notifications.completed, notifyUser: e.target.checked },
                  })}
                />
                The people who filled it, including who started it
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="checkbox checkbox-sm"
                  checked={notifications.completed.notifyFan}
                  onChange={(e) => onNotifications({
                    ...notifications,
                    completed: { ...notifications.completed, notifyFan: e.target.checked },
                  })}
                />
                Everyone else on the access levels above
              </label>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default FormAccessAndNotifications
